import { useState, useEffect, useCallback } from "react";
import sequenceApis from "../api/master/sequence";

interface UseSequenceNumberingOptions {
  /** Module name as configured in Sequence Master (e.g. "Project", "Task"). */
  moduleName: string;
  setValue: (name: string, value: any) => void;
  getValues?: (name: string) => any;
  /** Form field that holds the document number. Defaults to "referenceID". */
  fieldName?: string;
  /** Sequence scope level. PMS sequences are configured at "Project" level. */
  level?: string;
  /** Project the sequence is scoped to. Omit for top-level records. */
  projectId?: string | number | null;
  isEdit?: boolean;
  id?: string | null;
  /** Persist the chosen mode into the form (useful for multi-step forms). */
  persistModeToForm?: boolean;
}

/**
 * Drives auto vs. manual document numbering for a creation form.
 *
 * When an active sequence is configured for the module, the number is
 * auto-generated (field disabled, prefilled with the prefix). When no sequence
 * exists, the form falls back to manual entry so the user is never blocked from
 * creating a record. Ported from the buyer app's identical pattern and adapted
 * to the PMS `fetchSequence(level, projectId)` API signature.
 */
export default function useSequenceNumbering({
  moduleName,
  setValue,
  getValues,
  fieldName = "referenceID",
  level = "Project",
  projectId = null,
  isEdit = false,
  id,
  persistModeToForm = false,
}: UseSequenceNumberingOptions) {
  const [activeSequences, setActiveSequences] = useState<any[]>([]);
  const [numberingMode, setNumberingMode] = useState<"auto" | "manual">("auto");
  const [showNumberingModal, setShowNumberingModal] = useState(false);
  const [tempNumberingMode, setTempNumberingMode] = useState<"auto" | "manual">(
    "auto"
  );

  const getActiveSequence = useCallback(() => {
    if (activeSequences.length === 0) return null;
    const orgSequence = activeSequences.find((seq: any) => seq.level === "Org");
    return orgSequence || activeSequences[0];
  }, [activeSequences]);

  const currentSequence = getActiveSequence();
  const canAuto = activeSequences.length > 0;

  const fetchSequenceAccess = useCallback(async () => {
    try {
      const response = await sequenceApis.fetchSequence(level, projectId);
      const { data } = response || {};
      const today = new Date();
      const _sequenceAccess = (data || []).filter(
        (item: any) =>
          item.moduleName === moduleName &&
          new Date(item.endDate) > today &&
          (item.status === "Active" || item.status === undefined)
      );
      setActiveSequences(_sequenceAccess || []);

      // For multi-step forms, honour a mode already persisted in form state.
      if (persistModeToForm && getValues) {
        const existingMode = getValues("numberingMode");
        if (existingMode) {
          setNumberingMode(existingMode);
          return;
        }
      }

      const mode: "auto" | "manual" =
        _sequenceAccess && _sequenceAccess.length > 0 ? "auto" : "manual";
      setNumberingMode(mode);

      if (persistModeToForm) {
        setValue("numberingMode", mode);
      }
    } catch (error: any) {
      console.error("Error fetching sequences:", error);
      // No sequence reachable → fall back to manual so creation isn't blocked.
      setActiveSequences([]);
      setNumberingMode("manual");
      if (persistModeToForm) {
        setValue("numberingMode", "manual");
      }
    }
  }, [moduleName, level, projectId, persistModeToForm, getValues, setValue]);

  // Keep the field value in sync with the active numbering mode.
  useEffect(() => {
    const currentFieldValue = getValues ? getValues(fieldName) : "";
    if (numberingMode === "auto" && currentSequence && !isEdit && !id) {
      const { prefix } = currentSequence;
      if (!currentFieldValue || currentFieldValue === prefix) {
        setValue(fieldName, prefix);
      }
    } else if (numberingMode === "manual" && !isEdit && !id) {
      const isCurrentlyPrefix =
        currentSequence && currentFieldValue === currentSequence.prefix;
      if (isCurrentlyPrefix) {
        setValue(fieldName, "");
      }
    }
  }, [numberingMode, currentSequence, isEdit, id, fieldName, setValue, getValues]);

  const setEditMode = useCallback(() => {
    setNumberingMode("manual");
  }, []);

  const openModal = useCallback(() => {
    setTempNumberingMode(numberingMode);
    setShowNumberingModal(true);
  }, [numberingMode]);

  const closeModal = useCallback(() => {
    setShowNumberingModal(false);
  }, []);

  const saveModalSelection = useCallback(() => {
    if (tempNumberingMode === "auto" && !currentSequence) {
      return false; // caller should surface an error
    }

    setNumberingMode(tempNumberingMode);

    if (persistModeToForm) {
      setValue("numberingMode", tempNumberingMode);
    }

    if (tempNumberingMode === "auto" && currentSequence) {
      const { prefix } = currentSequence;
      setValue(fieldName, prefix);
    } else if (tempNumberingMode === "manual") {
      setValue(fieldName, "");
    }

    setShowNumberingModal(false);
    return true;
  }, [
    tempNumberingMode,
    currentSequence,
    setValue,
    fieldName,
    persistModeToForm,
  ]);

  // Strips the document-number field from the payload when auto-generating
  // (the server assigns it); keeps it when the user entered it manually.
  const buildPayload = useCallback(
    (data: any) => {
      const { [fieldName]: refId, ...rest } = data;
      const payload: any = { ...rest };
      if (numberingMode === "manual") {
        payload[fieldName] = refId;
      }
      return payload;
    },
    [numberingMode, fieldName]
  );

  return {
    numberingMode,
    setNumberingMode,
    showNumberingModal,
    tempNumberingMode,
    setTempNumberingMode,
    activeSequences,
    currentSequence,
    canAuto,
    fetchSequenceAccess,
    setEditMode,
    openModal,
    closeModal,
    saveModalSelection,
    buildPayload,
  };
}
